import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { Tag } from 'antd';

// Shared UI Library Components
import {
  CustomIcon,
  EIconName,
  EUserAccountStatus,
  ListEllipsis,
  CustomTooltip,
  // RatingElement,
  UserAvatar
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Components
import { ChangeProfileStatus } from '../../change-profile-status/change-profile-status';

// Styles
import './counsellors-card.scss';

// Models
import { ICounsellor } from '../../../../interfaces/counsellors.interfaces';

type TCounsellorsCardProps = {
  counsellor: ICounsellor;
  stripeConnected: boolean;
  resetList?: () => void;
}

export const CounsellorsCard: FC<TCounsellorsCardProps> = (
  {
    counsellor: {
      id,
      first_name,
      last_name,
      photo,
      worker_status,
      worker_company_status,
      message_rate,
      call_rate,
      audio_call_rate,
      conversation_rate,
      video,
      languages,
      // rating,
      // reviews_count,
      country,
      specialities,
      is_audio_call_enabled,
      is_video_call_enabled,
      is_conversation_enabled,
      is_messages_enabled,
      profile_completed,
      is_signup_completed,
      full_free_service_enabled,
    },
    stripeConnected,
    resetList = () => {},
  }: TCounsellorsCardProps
) => {

  const navigate = useNavigate();

  const navigateToCounsellorPage = (): void => {
    navigate(`/counsellors/${ id }`);
  }

  const getStatusByFieldValue = (): string => {
    if (worker_company_status === 'active') {
      return '';
    }
    if (worker_company_status === 'deactivated') {
      return 'Inactive';
    }
    if (worker_company_status === 'blocked') {
      return 'Blocked';
    }
    return '';
  }

  return (
    <div className="counsellors-card">
      <div className="counsellors-card--row">
        <div className="row-item row-item--avatar" onClick={ navigateToCounsellorPage }>
          <UserAvatar avatarUrl={ photo } sizeClass={ 'extra-large' } hasVideo={ !!video }
                      accountStatus={ worker_status as EUserAccountStatus }/>
        </div>
        <div className="row-item row-item--info" onClick={ navigateToCounsellorPage }>
          <div className="info-item info-item--speciality" hidden={ !specialities?.length }>
            <ListEllipsis items={ specialities.map(_ => _.name) } hasCounter={ true }/>
          </div>
          <div className="info-item info-item--name" onClick={ navigateToCounsellorPage }>
            { first_name } { last_name }
          </div>
          {/*<div className="info-item info-item--reviews">*/ }
          {/*  <RatingElement defaultValue={ rating } disabled={ true } size={ 'small' }/> { reviews_count } Reviews*/ }
          {/*</div>*/ }
        </div>
      </div>

      <div className="counsellors-card--row row-actions">
        <ChangeProfileStatus worker_company_status={ worker_company_status }
                             stripeConnected={ stripeConnected }
                             canActivate={ profile_completed && is_signup_completed }
                             counsellor_id={ id }
                             resetList={ resetList }
        />
      </div>

      <div className="counsellors-card--row">
        <div className="row-item row-item--status">
          <div className={ `status-item status-${ worker_company_status }` }>
            { getStatusByFieldValue() }
          </div>
        </div>
        <div className="row-item row-item--subinfo">
          <div className="subinfo-item subinfo-item--rates">
            { is_video_call_enabled &&
              <CustomTooltip placement="bottom" title={ 'Video cost per minute' } className="rates-item">
                <div className="rates-item--icon">
                  <CustomIcon name={ EIconName.CameraOn } size={ 'size-md' }/>
                </div>
                <div className="rates-item--text">
                  { !full_free_service_enabled && <>
                    <span>{ call_rate?.text ?? '$0' }</span>/min
                  </> }
                  { !!full_free_service_enabled && <>
                    <span>Free</span>
                  </> }
                </div>
              </CustomTooltip> }
            { is_audio_call_enabled &&
              <CustomTooltip placement="bottom" title={ 'Voice cost per minute' } className="rates-item">
                <div className="rates-item--icon">
                  <CustomIcon name={ EIconName.Call } size={ 'size-md' }/>
                </div>
                <div className="rates-item--text">
                  { !full_free_service_enabled && <>
                    <span>{ audio_call_rate?.text ?? '$0' }</span>/min
                  </> }
                  { !!full_free_service_enabled && <>
                    <span>Free</span>
                  </> }
                </div>
              </CustomTooltip> }
            { is_conversation_enabled &&
              <CustomTooltip placement="bottom" title={ 'Text Chat cost per minute' } className="rates-item">
                <div className="rates-item--icon">
                  <CustomIcon name={ EIconName.Chat } size={ 'size-md' }/>
                </div>
                <div className="rates-item--text">
                  { !full_free_service_enabled && <>
                    <span>{ conversation_rate?.text ?? '$0' }</span>/min
                  </> }
                  { !!full_free_service_enabled && <>
                    <span>Free</span>
                  </> }
                </div>
              </CustomTooltip> }
            { is_messages_enabled &&
              <CustomTooltip placement="bottom" title={ 'Correspondence cost per request' } className="rates-item">
                <div className="rates-item--icon">
                  <CustomIcon name={ EIconName.Message } size={ 'size-md' }/>
                </div>
                <div className="rates-item--text">
                  { !full_free_service_enabled && <>
                    <span>{ message_rate?.text ?? '$0' }</span>/rqst
                  </> }
                  { !!full_free_service_enabled && <>
                    <span>Free</span>
                  </> }
                </div>
              </CustomTooltip> }
          </div>
          <div className="subinfo-item subinfo-item--languages">
            { country && <CustomTooltip title={ country?.name } placement={ 'top' }>
              <img src={ country?.flag } alt={ country?.name }/>
            </CustomTooltip> }
            { !!languages.length && languages.map((lang, idx) => <Tag key={ idx }>{ lang?.name }</Tag>) }
          </div>
        </div>
      </div>
    </div>
  );
}
